import placeholder from "../../../assets/mk8dx-logo.webp";
import { useState } from "react";
import "./Track.css";
import trackToString from "../../functions/trackToString";

/**
 * Individual track component with support for different display modes.
 * 
 * @param {Object} props
 * @param {Object} props.track - Track data object
 * @param {number} props.index - Track index in the cup
 * @param {string} props.size - Display size: 'big' | 'medium' | 'small'
 * @param {boolean} props.showCursor - Whether to show pointer cursor
 * @param {number} props.tabIndex - Tab index for keyboard navigation
 * @param {function} props.addToCup - Callback when track is clicked/selected
 * @param {boolean} props.yourCup - Whether this is in the user's cup (disables interaction)
 * @param {number} props.selectTrack - Current selection state (5 = disabled)
 * @param {boolean} props.select - Whether this track is currently selected
 * @param {boolean} props.showEmblem - Whether to show cup emblem badge
 */
export default function Track(props) {
    const [hover, setHover] = useState(false);
    const placeholderText = "Select a track below";
    
    // Determine if track is interactive
    const isInteractive = !props.yourCup && props.selectTrack !== 5;
    
    // Show highlight on hover/focus or when selected
    const shouldHighlight = (isInteractive && hover) || props.select;
    
    // Handle click
    const handleClick = () => {
        if (isInteractive) {
            props.addToCup?.(props.index);
        }
    };
    
    // Handle keyboard interaction
    const handleKeyDown = (e) => {
        if ((e.key === "Enter" || e.key === " ") && isInteractive) {
            e.preventDefault();
            props.addToCup?.(props.index);
        }
    };

    return (
            <div
                className={`prevent-select track-container-medium`}
                role={props.tabIndex === 0 ? "button" : undefined}
                tabIndex={props.tabIndex}
                id={`${props.yourCup ? "user" : "display"}-${props.index + 1}`}
                style={{
                    cursor: props.showCursor ? "pointer" : "auto",
                }}
                title={props.track ? trackToString(props.track) : placeholderText}
                onPointerEnter={() => setHover(true)}
                onPointerLeave={() => setHover(false)}
                onFocus={() => setHover(true)}
                onBlur={() => setHover(false)}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                {/* Cup emblem badge */}
                {props.showEmblem && props.track?.emblem && (
                    <div>
                        <img
                            src={props.track.emblem}
                            alt={`${props.track.cup.name} cup emblem`}
                            className="track-emblem"
                            crossOrigin="anonymous"
                        />
                    </div>
                )}

                {/* Track image */}
                <img 
                    src={props.track?.image || placeholder} 
                    style={{ width: "100%", height: "auto" }} 
                    alt={props.track?.name || placeholderText}
                    crossOrigin="anonymous"
                />

                {/* Track info overlay */}
                <div className="track-info">
                    {/* Origin badge (game/DLC) */}
                    {props.track?.origin && (
                        <p 
                            className={`track-origin-${props.size || "medium"}`}
                            style={{
                                background: shouldHighlight ? "#FFED00" : "white"
                            }}
                        >
                            {props.track.origin}
                        </p>
                    )}

                    {/* Track name */}
                    <p 
                        className={`track-name-${props.size || "medium"}`}
                        style={{
                            background: shouldHighlight ? "#FFED00" : "white"
                        }}
                    >
                        {props.track ? props.track.name : placeholderText}
                    </p>
                </div>
            </div>
    );
}